// WebSocketContext.tsx
import React, { createContext, useEffect, useState, useContext } from 'react'
import { Snackbar } from '@mui/material'
import { io, Socket } from 'socket.io-client'
import Slide from '@mui/material/Slide'
import { TransitionProps } from '@mui/material/transitions'

interface WebSocketContextType {
  socket: Socket | null
  isConnected: boolean
  error: string | null
  resetSocket: () => void
}
const WebSocketContext = createContext<WebSocketContextType | null>(null)

export const WebSocketProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [socket, setSocket] = useState<Socket | null>(null)
  const [isConnected, setIsConnected] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [state, setState] = React.useState<{
    open: boolean
    Transition: React.ComponentType<
      TransitionProps & {
        children: React.ReactElement
      }
    >
  }>({
    open: isConnected,
    Transition: Slide,
  })

  const handleClose = () => {
    setState({
      ...state,
      open: false,
    })
  }

  const initializeSocket = () => {
    const newSocket = io(process.env.REACT_APP_BASE_URL, {
      auth: {
        token: localStorage.getItem('token') ?? '',
      },
    })

    // Handle connection
    newSocket.on('connect', () => {
      setIsConnected(true)
      setError(null)
      console.log('Connected to WebSocket server')
    })

    // Handle disconnection
    newSocket.on('disconnect', (reason) => {
      setIsConnected(false)
      console.log('Disconnected from WebSocket server:', reason)
      if (reason === 'io server disconnect') {
        // The disconnection was initiated by the server, you need to reconnect manually
        newSocket.connect()
      }
    })

    // Handle connection error
    newSocket.on('connect_error', (err) => {
      setError(err.message)
      console.log('Connection error:', err.message)
    })

    setSocket(newSocket)
    return newSocket
  }

  const resetSocket = () => {
    if (socket) {
      socket.disconnect()
    }
    initializeSocket()
  }

  useEffect(() => {
    const currentSocket = initializeSocket()
    return () => {
      currentSocket.disconnect()
    }
  }, [])

  return (
    <WebSocketContext.Provider
      value={{ socket, isConnected, error, resetSocket }}
    >
      {localStorage.getItem('token') && error && (
        <Snackbar
          open={!isConnected}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
          ContentProps={{
            sx: {
              display: 'block',
              textAlign: 'center',
            },
          }}
          onClose={handleClose}
          TransitionComponent={state.Transition}
          message="Please connect to internet"
          key={state.Transition.name}
          autoHideDuration={1200}
        />
      )}
      {children}
    </WebSocketContext.Provider>
  )
}

export const useWebSocket = () => {
  const context = useContext(WebSocketContext)
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider')
  }
  return context
}
